import { useMemo } from 'react'
import {
  Badge,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  MediumText,
  StrongText,
  Subheading,
  SurfaceCard,
  SurfacePanel,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
  Text,
} from '../../../components/ui'
import { ArrowTrendingUpIcon, MinusIcon } from '@heroicons/react/20/solid'
import type { DashboardMetrics, HealthRecord, Report, Task } from '../types'
import { TREND_SERIES, VITAL_SIGNS, VITALS_HISTORY } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'
import EcgWaveform from './EcgWaveform'

const MAX_TREND_VALUE = Math.max(...TREND_SERIES.map((point) => point.value))
const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const BAR_TRACK = uiClasses.barTrack

type VitalTrend = (typeof VITAL_SIGNS)[number]['trend']

interface TrendIconProps {
  trend: VitalTrend
}

interface HealthMetricsTabProps {
  metrics: DashboardMetrics
  reports: Report[]
  records: HealthRecord[]
  tasks: Task[]
  taskProgress: number
}

function TrendIcon({ trend }: TrendIconProps) {
  if (trend === 'improving') {
    return <ArrowTrendingUpIcon className="size-3.5 dash-viz-text-accent" />
  }

  return <MinusIcon className={`size-3.5 ${TEXT_FAINT}`} />
}

export default function HealthMetricsTab({
  metrics,
  reports,
  records,
  tasks,
  taskProgress,
}: HealthMetricsTabProps) {
  const latestParsedRecord = useMemo(
    () => records.find((record) => record.status === 'Parsed') ?? null,
    [records]
  )
  const parsedRecords = records.filter((record) => record.status === 'Parsed').length
  const pendingTasks = tasks.filter((task) => !task.done).length
  const completedTasks = tasks.length - pendingTasks

  const overviewStats = useMemo(
    () => [
      { label: 'Profile completeness', value: `${metrics.profileCompleteness}%` },
      { label: 'Reports generated', value: reports.length.toString() },
      { label: 'Parsed records', value: parsedRecords.toString() },
      { label: 'Open tasks', value: pendingTasks.toString() },
    ],
    [metrics.profileCompleteness, parsedRecords, pendingTasks, reports.length]
  )

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="health-overview-shell">
        <div className="health-overview-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Health signal board</Subheading>
              <Badge variant="zinc">
                {taskProgress}% plan progress
              </Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge variant="zinc">
                {completedTasks}/{tasks.length} tasks complete
              </Badge>
              <Badge variant="zinc">
                {metrics.connectedProviders} providers connected
              </Badge>
              <Badge variant="zinc">
                {metrics.pendingRecords} records pending
              </Badge>
            </div>
          </div>

          <div className="health-inline-metrics">
            {overviewStats.map((stat, index) => (
              <GradientSphereMetricCard
                key={stat.label}
                label={stat.label}
                value={stat.value}
                sphereColor={sphereColorAt(index)}
              />
            ))}
          </div>
        </div>

      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Vital signals</Subheading>
          <Badge variant="zinc">
            Live vitals overview
          </Badge>
        </div>

        <div className="health-vitals-stream mt-4">
          {VITAL_SIGNS.map((vital) => (
            <div key={vital.id} className="health-vital-item">
              <div className="flex items-center gap-2">
                <TrendIcon trend={vital.trend} />
                <StrongText as="span">{vital.label}</StrongText>
              </div>
              <StrongText size="lg">
                {vital.value}
                <MediumText as="span" className="ml-1" tone="faint">{vital.unit}</MediumText>
              </StrongText>
            </div>
          ))}
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="health-signal-layout">
          <section>
            <Subheading variant="dashboard">Trend score (6 months)</Subheading>
            <Text className="mt-2">Longitudinal profile trend rendered from parsed health data.</Text>
            <div className="mt-6 flex h-52 items-end gap-3">
              {TREND_SERIES.map((point) => {
                const height = Math.max(12, (point.value / MAX_TREND_VALUE) * 100)
                return (
                  <div key={point.label} className="flex flex-1 flex-col items-center gap-2">
                    <div
                      className="w-full rounded-t-xl bg-linear-to-t from-accent to-accent-soft"
                      style={{ height: `${height}%` }}
                    />
                    <span className={`text-xs/5 ${TEXT_SUBTLE}`}>{point.label}</span>
                  </div>
                )
              })}
            </div>
          </section>

          <SurfacePanel as="section">
            <Subheading variant="dashboard">ECG — 12-lead resting</Subheading>
            <Text className="mt-2">Normal sinus rhythm · 72 bpm · QTc 410 ms</Text>
            <div className="mt-6 h-52 dash-viz-text-accent">
              <EcgWaveform />
            </div>
          </SurfacePanel>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="health-care-layout">
          <section>
            <Subheading variant="dashboard">Vitals history</Subheading>
            <Table
              className="mt-4"
              surface
              striped
            >
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Heart Rate</TableHead>
                  <TableHead>Blood Pressure</TableHead>
                  <TableHead>SpO2</TableHead>
                  <TableHead>HRV</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {VITALS_HISTORY.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className={`font-medium ${TEXT_PRIMARY}`}>{row.date}</TableCell>
                    <TableCell>{row.heartRate} bpm</TableCell>
                    <TableCell>{row.bp}</TableCell>
                    <TableCell>{row.spo2}%</TableCell>
                    <TableCell>{row.hrv} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <aside className="space-y-3">
            <SurfacePanel as="section">
              <Subheading variant="dashboard">Care plan progress</Subheading>
              <div className={`mt-4 h-3 w-full rounded-full ${BAR_TRACK}`}>
                <div
                  className="h-3 rounded-full bg-linear-to-r from-accent to-accent-soft"
                  style={{ width: `${taskProgress}%` }}
                />
              </div>
              <p className={`mt-3 text-sm/6 ${TEXT_SECONDARY}`}>
                {taskProgress}% complete · {pendingTasks} tasks remaining
              </p>
            </SurfacePanel>

            <SurfacePanel as="section">
              <Subheading variant="dashboard">Member profile</Subheading>
              <DescriptionList className="mt-4">
                <DescriptionTerm>Profile completeness</DescriptionTerm>
                <DescriptionDetails>{metrics.profileCompleteness}%</DescriptionDetails>
                <DescriptionTerm>Most recent report</DescriptionTerm>
                <DescriptionDetails>{reports[0]?.title ?? 'No report yet'}</DescriptionDetails>
                <DescriptionTerm>Latest parsed record</DescriptionTerm>
                <DescriptionDetails>{latestParsedRecord?.title ?? 'No parsed records yet'}</DescriptionDetails>
              </DescriptionList>
            </SurfacePanel>
          </aside>
        </div>
      </SurfaceCard>
    </div>
  )
}
